import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Recycle, Car, Users, ZapOff, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const Sustainability = () => {
  return (
    <>
      <section className="pt-[160px] pb-20 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-6">
          [ Sustainability · PPN 06/21 ]
        </div>
        <h1 className="font-display font-bold text-[3rem] md:text-[5rem] leading-[0.98] tracking-tightest max-w-5xl">
          A measured path to
          <span className="text-brand"> Net Zero by 2050.</span>
        </h1>
        <p className="mt-8 text-[18px] text-muted-foreground max-w-2xl leading-relaxed">
          Calmbreeze’s Carbon Reduction Plan is published in line with PPN 06/21 and aligned to the
          UK Government’s Net Zero target. We’re treating sustainability the same way we treat
          compliance — methodically, with documented progress.
        </p>
      </section>

      <section className="pb-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-paper-warm rounded-md p-10 border border-black/5">
            <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">2025–2030</div>
            <div className="font-mono text-accent text-[3.5rem] leading-none font-medium tracking-tightest mb-3">15%</div>
            <p className="text-[14px] text-muted-foreground leading-relaxed">
              Reduction in Scope 1 & Scope 2 emissions.
            </p>
          </div>
          <div className="bg-paper-warm rounded-md p-10 border border-black/5">
            <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">2030–2040</div>
            <div className="font-mono text-accent text-[3.5rem] leading-none font-medium tracking-tightest mb-3">50%</div>
            <p className="text-[14px] text-muted-foreground leading-relaxed">
              Reduction in total emissions across all scopes.
            </p>
          </div>
          <div className="bg-paper-warm rounded-md p-10 border border-black/5">
            <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">2050</div>
            <div className="font-mono text-brand text-[3.5rem] leading-none font-medium tracking-tightest mb-3">Net Zero</div>
            <p className="text-[14px] text-muted-foreground leading-relaxed">
              Through innovation, offsets and continued reduction.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-4">[ Initiatives ]</div>
            <h2 className="font-display font-bold text-[2rem] md:text-[3rem] tracking-tightest leading-[1.05]">
              Five reduction streams in motion.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-[16px] text-muted-foreground leading-relaxed">
              Our reduction work is split across operations, fleet, supply chain, employee
              engagement and waste — with annual progress reviews to keep us honest.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2">
          {[
            { Icon: Leaf, title: "Energy efficiency", text: "Switching to high-efficiency HVAC; renewable-energy office operations by 2027." },
            { Icon: Car, title: "Fleet electrification", text: "Hybrid or fully electric company vehicles by 2030." },
            { Icon: Users, title: "Sustainable supply chain", text: "Partnering with low-carbon suppliers and tier-1 vendors with science-based targets." },
            { Icon: ZapOff, title: "Reduced commute emissions", text: "Hybrid working policy and education programmes for staff." },
            { Icon: Recycle, title: "Zero-to-landfill waste", text: "Optimised recycling, refrigerant reclaim and waste oil management." },
            { Icon: Leaf, title: "Annual transparent reporting", text: "Baselined in 2025 with annual review against PPN 06/21." },
          ].map(({ Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 60} className="border-t border-black/8 py-8">
              <Icon className="h-5 w-5 text-brand mb-5" strokeWidth={1.6} />
              <div className="font-display font-semibold text-[1.1rem] tracking-tight mb-2">{title}</div>
              <p className="text-[13px] text-muted-foreground leading-relaxed">{text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 bg-paper-cream">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12 text-center">
          <p className="text-[20px] md:text-[28px] font-serif italic tracking-tight leading-snug max-w-3xl mx-auto text-ink">
            &ldquo;Calmbreeze is committed to achieving Net Zero emissions by 2050,
            <span className="text-brand not-italic font-display font-bold"> in alignment with the UK Government&rsquo;s target.&rdquo;</span>
          </p>
          <Link
            to="/contact"
            data-testid="sustain-cta-contact"
            className="mt-10 inline-flex items-center gap-2 px-7 py-4 bg-brand text-white font-semibold text-[14px] rounded-sm hover:bg-accent transition-colors"
          >
            Discuss your estate <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Sustainability;
